import React, { useEffect, useState } from 'react';
import { api } from '@/api/client';
import { workspacePickersAsUsers } from '@/api/services/workspaceUsers';
import type { Search, User } from '@/types';
import { formatApiError } from '@/utils/formatApiError';

type Props = {
  search: Search;
  currentUserId?: string | null;
  onClose: () => void;
  onTransferred: (search: Search) => void;
};

function displayUser(user: User): string {
  return user.name?.trim() || user.email || user.id;
}

export default function SearchTransferDialog({
  search,
  currentUserId,
  onClose,
  onTransferred,
}: Props) {
  const [users, setUsers] = useState<User[]>([]);
  const [newOwnerId, setNewOwnerId] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const pickers = await api.workspaceUsers.pickers();
        if (!cancelled) {
          setUsers(
            workspacePickersAsUsers(pickers.items).filter((u) => u.id !== search.user_id && u.id !== currentUserId && u.is_enabled !== false),
          );
        }
      } catch (err) {
        if (!cancelled) {
          setError(formatApiError(err, 'Failed to load workspace users'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUserId, search.user_id]);

  async function transfer(e: React.FormEvent) {
    e.preventDefault();
    if (!newOwnerId) return;
    setSubmitting(true);
    setError('');
    try {
      const updated = await api.searches.transfer(search.id, newOwnerId);
      onTransferred(updated);
      onClose();
    } catch (err) {
      setError(formatApiError(err, 'Transfer failed'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div className="modal card card-elevated" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="transfer-search-title">
        <h2 id="transfer-search-title">Transfer ownership</h2>
        <p className="muted modal-hint">
          Transfer <strong>{search.name}</strong> to another team member. You will lose owner controls unless
          granted ACL manage access.
        </p>
        {error ? <div className="alert alert-error">{error}</div> : null}
        <form className="admin-form-grid" onSubmit={(e) => void transfer(e)}>
          <label className="admin-form-grid__full">
            New owner
            <select
              value={newOwnerId}
              onChange={(e) => setNewOwnerId(e.target.value)}
              required
              disabled={loading || submitting}
            >
              <option value="">{loading ? 'Loading team…' : 'Select team member…'}</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {displayUser(u)} ({u.role})
                </option>
              ))}
            </select>
          </label>
          <div className="admin-form-grid__full modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn-danger" disabled={submitting || !newOwnerId}>
              {submitting ? 'Transferring…' : 'Transfer ownership'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
